import type { ChargePoint } from '../types';

export type PowerCategoryId = 'powerstart' | 'velocity' | 'megacharge';

export interface PowerCategoryConfig {
  id: PowerCategoryId;
  name: string;
  subtitle: string;
  minKw: number;
  maxKw: number;
  color: string;
  boltCount: number;
  bgClass: string;
  textClass: string;
  borderClass: string;
}

export const POWER_CATEGORIES: PowerCategoryConfig[] = [
  {
    id: 'powerstart',
    name: 'PowerStart',
    subtitle: '< 40 kW',
    minKw: 0,
    maxKw: 39.999,
    color: '#22c55e',
    boltCount: 1,
    bgClass: 'bg-green-500',
    textClass: 'text-green-500',
    borderClass: 'border-green-500',
  },
  {
    id: 'velocity',
    name: 'Velocity',
    subtitle: '40–119 kW',
    minKw: 40,
    maxKw: 119.999,
    color: '#f97316',
    boltCount: 2,
    bgClass: 'bg-orange-500',
    textClass: 'text-orange-500',
    borderClass: 'border-orange-500',
  },
  {
    id: 'megacharge',
    name: 'Mega Charge',
    subtitle: '≥ 120 kW',
    minKw: 120,
    maxKw: Infinity,
    color: '#a855f7',
    boltCount: 3,
    bgClass: 'bg-purple-500',
    textClass: 'text-purple-500',
    borderClass: 'border-purple-500',
  },
];

export function getPowerCategory(kw: number): PowerCategoryId {
  if (kw >= 120) return 'megacharge';
  if (kw >= 40) return 'velocity';
  return 'powerstart';
}

export function getPowerCategoryConfig(id: PowerCategoryId): PowerCategoryConfig {
  return POWER_CATEGORIES.find((c) => c.id === id)!;
}

/**
 * Returns the highest power tier at a location by checking EVSE maxOutputPowerKw
 * on active EVSEs, falling back to parsing chargepoint.power string.
 */
export function getLocationMaxPowerCategory(chargepoints: ChargePoint[]): PowerCategoryConfig {
  let maxKw = 0;

  for (const cp of chargepoints) {
    // Check EVSEs first
    if (cp.evses && cp.evses.length > 0) {
      for (const evse of cp.evses) {
        if (evse.isActive && evse.maxOutputPowerKw > maxKw) {
          maxKw = evse.maxOutputPowerKw;
        }
      }
    } else {
      // Fallback: parse power string (e.g. "22 kW", "150kW")
      const parsed = parseFloat(cp.power);
      if (!isNaN(parsed) && parsed > maxKw) {
        maxKw = parsed;
      }
    }
  }

  return getPowerCategoryConfig(getPowerCategory(maxKw));
}

/**
 * Returns true if any active EVSE at the location falls within any of the
 * selected power categories. Returns true if no categories are selected.
 */
export function locationMatchesPowerCategories(
  chargepoints: ChargePoint[],
  selectedCategories: PowerCategoryId[]
): boolean {
  if (selectedCategories.length === 0) return true;

  const selectedConfigs = selectedCategories.map(getPowerCategoryConfig);

  for (const cp of chargepoints) {
    if (cp.evses && cp.evses.length > 0) {
      for (const evse of cp.evses) {
        if (!evse.isActive) continue;
        const kw = evse.maxOutputPowerKw;
        if (selectedConfigs.some((c) => kw >= c.minKw && kw <= c.maxKw)) {
          return true;
        }
      }
    } else {
      const kw = parseFloat(cp.power);
      if (!isNaN(kw) && selectedConfigs.some((c) => kw >= c.minKw && kw <= c.maxKw)) {
        return true;
      }
    }
  }

  return false;
}
